import {
  IForumBoardPermission,
  IForumCommentTopicDetail,
  IForumMessageCommentTopicDetail,
  IForumMessageTopicDetail,
  IForumTopicDetail,
  ITopicSearchTitle,
} from 'redhotpie-shared-lib/interfaces/IForum';
import { GIF_LOADING } from '../api/GiphyAPIs';
import { COMMENT_MESSAGE_CONTENT_TYPE, IMAGE_ACTION_TYPE } from '../constants/constants';
import * as utils from '../functions/utils';
import travelPlan from './travelPlans';
import { IPaginationData } from 'redhotpie-shared-lib/interfaces/IUtils';
import { IMainPhoto } from 'redhotpie-shared-lib/interfaces/IUser';
import { PagingInfo } from './pagingInfo';

export default class forumTopicDetails {
  topic: ITopicSearchTitle;
  messages: IForumMessageTopicDetail[];
  paginationInfo: IPaginationData;
  permission: IForumBoardPermission;
  constructor() {
    this.topic = {
      poster: undefined,
      id: -1,
      title: '',
      message: '',
      datePosted: '',
      replyCount: 0,
      isFollowing: false,
      isGlobal: false,
      keyWords: [],
      isSticky: false,
      isLiked: false,
      likeCount: 0,
      isClosed: false,
      seoURL: '',
    };
    this.messages = [];
    this.paginationInfo = {
      totalItems: 0,
      totalPages: 0,
      size: 0,
      page: 0,
    };
    this.permission = {
      canPost: false,
      canReply: false,
    };
  }

  static mappingObject(_obj: any): IForumTopicDetail {
    if (_obj) {
      let obj = Object.assign(new forumTopicDetails(), _obj);
      obj.topic.poster = _obj.Topic?.Poster ? travelPlan.mappingProfile(_obj.Topic?.Poster) : {};
      obj.topic.id = _obj.Topic?.ID;
      obj.topic.title = _obj.Topic?.Title;
      obj.topic.message = _obj.Topic?.Message;
      obj.topic.datePosted = _obj.Topic?.DatePosted;
      obj.topic.replyCount = _obj.Topic?.ReplyCount;
      obj.topic.isFollowing = _obj.Topic?.IsFollowing;
      obj.topic.isGlobal = _obj.Topic?.IsGlobal;
      obj.topic.keyWords = _obj.Topic?.Keywords;
      obj.topic.isSticky = _obj.Topic?.IsSticky;
      obj.topic.isLiked = _obj.Topic?.IsLiked;
      obj.topic.likeCount = _obj.Topic?.LikeCount;
      obj.topic.isClosed = _obj.Topic?.IsClosed || false;
      obj.topic.seoURL = _obj.Topic?.SEOUrl || null;

      //
      obj.paginationInfo.totalItems = _obj.PaginationInfo && _obj.PaginationInfo.TotalItems;
      obj.paginationInfo.totalPages = _obj.PaginationInfo && _obj.PaginationInfo.TotalPages;
      obj.paginationInfo.size = _obj.PaginationInfo && _obj.PaginationInfo.Size;
      obj.paginationInfo.page = _obj.PaginationInfo && _obj.PaginationInfo.Page;
      //
      obj.permission.canPost = (_obj.Permission && _obj.Permission.CanPostTopic) || false;
      obj.permission.canReply = (_obj.Permission && _obj.Permission.CanReplyToTopic) || false;
      //
      obj.messages = _obj.Messages && _obj.Messages.length > 0 ? forumTopicDetails.mappingMessageObjects(_obj.Messages) : [];
      obj = utils.cleanObjectKeys(Object.keys(new forumTopicDetails()), obj); // clean other key doesn't exist in user model
      return obj;
    }
    return new forumTopicDetails();
  }

  static mappingMessageObject(_obj: any): IForumMessageTopicDetail {
    if (!_obj) return {} as IForumMessageTopicDetail;

    const {
      Poster,
      ID,
      Title,
      Message,
      DatePosted,
      ReplyCount,
      IsLiked,
      LikeCount,
      MessageContentType,
      PaginationInfo,
      GiphyImage,
      ImageContent,
    } = _obj;

    const poster = Poster ? travelPlan.mappingProfile(Poster) : undefined;
    const paginationInfo = PaginationInfo
      ? PagingInfo.mappingObject(PaginationInfo)
      : {
          totalItems: 0,
          totalPages: 0,
          size: 0,
          page: 0,
        };

    let imageContent: any = {};
    if (MessageContentType === COMMENT_MESSAGE_CONTENT_TYPE.GIFFY && GiphyImage) {
      imageContent = {
        fullUrl: GIF_LOADING.replace('{id}', GiphyImage.GiphyImageID),
        width: GiphyImage.Width,
        height: GiphyImage.Height,
      };
    } else if (MessageContentType === COMMENT_MESSAGE_CONTENT_TYPE.IMAGE && ImageContent) {
      imageContent = forumTopicDetails.mappingObjectImage(ImageContent);
    }

    return {
      poster,
      id: ID || -1,
      title: Title || '',
      message: Message || '',
      messageContentType: MessageContentType || COMMENT_MESSAGE_CONTENT_TYPE.TEXT,
      datePosted: DatePosted || '',
      replyCount: ReplyCount || 0,
      isLiked: !!IsLiked,
      likeCount: LikeCount || 0,
      imageContent,
      comments: [],
      paginationInfo,
      paging: 0,
    };
  }

  static mappingMessageObjects(_data: any[]): IForumMessageTopicDetail[] {
    const data: IForumMessageTopicDetail[] = [];
    _data.forEach(item => {
      const _item = forumTopicDetails.mappingMessageObject(item);
      data.push(_item);
    });
    return data;
  }

  static mappingObjectsComment(_data: any[]): IForumCommentTopicDetail[] {
    const data: IForumCommentTopicDetail[] = [];
    _data.forEach(item => {
      const _item = forumTopicDetails.mappingComment(item);
      data.push(_item);
    });
    return data;
  }

  static mappingObjectImage(_photo: any): IMainPhoto {
    //   photo.photoId = _photo.PhotoID

    return {
      thumbUrl: _photo.ThumbUrl,
      fullUrl: _photo.FullUrl,
      height: _photo.Height,
      width: _photo.Width,
      isRestricted: _photo.IsRestricted,
      classification: _photo.Classification,
      imageActionType: _photo.ImageActionType || IMAGE_ACTION_TYPE.SHOW_IMAGE,
      showSafeMode: _photo.ShowSafeMode || false,
    };
  }

  static mappingComment(_obj: any): IForumCommentTopicDetail {
    if (!_obj) return {} as IForumCommentTopicDetail;

    const {
      Poster,
      ID,
      Title,
      Message,
      DatePosted,
      ReplyCount,
      IsLiked,
      LikeCount,
      MessageContentType,
      ParentID,
      QuoteTitle,
      QuoteMsg,
      GiphyImage,
      ImageContent,
    } = _obj;

    const poster = Poster ? travelPlan.mappingProfile(Poster) : undefined;
    let imageContent: any = {};

    if (MessageContentType === COMMENT_MESSAGE_CONTENT_TYPE.GIFFY && GiphyImage) {
      imageContent = {
        fullUrl: GIF_LOADING.replace('{id}', GiphyImage.GiphyImageID),
        width: GiphyImage.Width,
        height: GiphyImage.Height,
      };
    } else if (ImageContent) {
      imageContent = forumTopicDetails.mappingObjectImage(ImageContent);
    }

    return {
      poster,
      id: ID || -1,
      title: Title || '',
      message: Message || '',
      messageContentType: MessageContentType || COMMENT_MESSAGE_CONTENT_TYPE.TEXT,
      datePosted: DatePosted || '',
      replyCount: ReplyCount || 0,
      isLiked: !!IsLiked,
      likeCount: LikeCount || 0,
      imageContent,
      parentID: ParentID || -1,
      quoteTitle: QuoteTitle || '',
      quoteMsg: QuoteMsg || '',
    };
  }

  static mappingMessageComment(_obj: any): IForumMessageCommentTopicDetail {
    const obj: IForumMessageCommentTopicDetail = {
      paginationInfo: {
        totalItems: 0,
        totalPages: 0,
        size: 0,
        page: 0,
      },
      comments: [],
    };
    obj.comments = _obj && _obj.Comments && _obj.Comments.length > 0 ? forumTopicDetails.mappingObjectsComment(_obj.Comments) : [];
    obj.paginationInfo.totalItems = _obj.PaginationInfo && _obj.PaginationInfo.TotalItems;
    obj.paginationInfo.totalPages = _obj.PaginationInfo && _obj.PaginationInfo.TotalPages;
    obj.paginationInfo.size = _obj.PaginationInfo && _obj.PaginationInfo.Size;
    obj.paginationInfo.page = _obj.PaginationInfo && _obj.PaginationInfo.Page;
    return obj;
  }
}
